import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTracking } from '../hooks/useTracking';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { 
  Globe, 
  LogOut, 
  User, 
  MessageSquare, 
  Shield, 
  Menu, 
  X, 
  Search,
  ChevronDown,
  Package,
  Users,
  FileText,
  Home,
  ShoppingCart,
  ShoppingBag,
  Store,
  Heart
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Navbar = () => {
  const { user, profile, signOut } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { unreadMessages, unreadProposals, totalNotifications } = useNotifications();
  const { trackSearch } = useTracking();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('products');
  const [cartCount, setCartCount] = useState(0);

  // Update cart count from localStorage
  React.useEffect(() => {
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem('rarifex_cart') || '[]');
      setCartCount(cart.length);
    };
    
    updateCartCount();
    
    // Listen for cart updates
    window.addEventListener('cartUpdated', updateCartCount);
    window.addEventListener('storage', updateCartCount);
    
    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
      window.removeEventListener('storage', updateCartCount);
    };
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Track search for recommendations
      trackSearch(searchQuery.trim());
      
      if (searchType === 'products') {
        navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      } else {
        navigate(`/crafters?search=${encodeURIComponent(searchQuery.trim())}`);
      }
      setSearchQuery('');
    }
  };

  return (
    <nav className="relative z-50 bg-white border-b-0 shadow-sm">
      {/* Tricolore stripe at very top */}
      <div className="flex h-1">
        <div className="flex-1 bg-[#009246]" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-[#CE2B37]" />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-14 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="navbar-logo">
            <img 
              src="/rarifex-logo.png" 
              alt="Rarifex" 
              className="h-8 sm:h-9 w-auto"
            />
            <span className="font-bold text-xl sm:text-2xl tracking-tight" style={{fontFamily: 'Inter, sans-serif', color: '#008080', letterSpacing: '-0.01em'}}>
              RARIFEX
            </span>
          </Link>

          {/* Search Bar - Desktop - Centered */}
          <form onSubmit={handleSearch} className="hidden md:flex absolute left-1/2 transform -translate-x-1/2" style={{width: 'calc(33.33% - 20px)'}}>
            <div className="flex w-full h-9">
              {/* Search Type Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    type="button"
                    variant="outline" 
                    className="rounded-r-none border-r-0 bg-slate-50 hover:bg-slate-100 px-3 min-w-[120px] justify-between h-9"
                  >
                    <span className="flex items-center gap-1.5">
                      {searchType === 'products' ? (
                        <>
                          <Package className="w-4 h-4" />
                          <span className="text-sm">Prodotti</span>
                        </>
                      ) : (
                        <>
                          <Users className="w-4 h-4" />
                          <span className="text-sm">Artigiani</span>
                        </>
                      )}
                    </span>
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => setSearchType('products')}>
                    <Package className="w-4 h-4 mr-2" />
                    Prodotti
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSearchType('crafters')}>
                    <Users className="w-4 h-4 mr-2" />
                    Artigiani
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Search Input */}
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchType === 'products' ? "Cerca prodotti artigianali..." : "Cerca artigiani..."}
                className="rounded-none border-x-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-9 flex-1"
                data-testid="search-input"
              />
              
              {/* Search Button */}
              <Button 
                type="submit"
                className="rounded-l-none bg-[#008080] hover:bg-[#006666] px-4 h-9"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </form>

          {/* Right side actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Language Switcher - HIDDEN FOR ITALY-ONLY LAUNCH - Restore when implementing i18n */}
            {/* <div className="hidden sm:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-9" data-testid="language-switcher">
                    <Globe className="w-4 h-4" />
                    <span className="ml-1 uppercase text-xs">{language}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setLanguage('it')} data-testid="lang-it">
                    Italiano
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="lang-en">
                    English
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div> */}

            {/* User menu or Auth buttons */}
            {user ? (
              <>
                {/* Cart Button with Badge */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/cart')}
                  className="relative h-9"
                  data-testid="nav-cart"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-[#008080] text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="relative h-9" data-testid="user-menu-trigger">
                      <User className="w-4 h-4" />
                      <span className="ml-2 hidden lg:inline max-w-[100px] truncate">{profile?.full_name || user.email}</span>
                      {totalNotifications > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-[#DAA520] text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                          {totalNotifications > 99 ? '99+' : totalNotifications}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate('/client/dashboard?tab=cliente')} data-testid="nav-area-cliente">
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Area Cliente
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/crafter/dashboard?tab=artigiano')} data-testid="nav-area-artigiano">
                      <Store className="w-4 h-4 mr-2" />
                      Area Artigiano
                      {unreadProposals > 0 && (
                        <Badge className="ml-2 bg-[#DAA520] text-white text-xs">{unreadProposals}</Badge>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="nav-favorites">
                      <Heart className="w-4 h-4 mr-2" />
                      Preferiti
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="nav-messages-menu">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Messaggi
                      {unreadMessages > 0 && (
                        <Badge className="ml-2 bg-red-500 text-white text-xs">{unreadMessages}</Badge>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="nav-profile">
                      <User className="w-4 h-4 mr-2" />
                      {t('profile')}
                    </DropdownMenuItem>
                    {profile?.is_admin && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin" className="text-red-600">
                        <Shield className="w-4 h-4 mr-2" />
                        SuperAdmin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleSignOut} data-testid="nav-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                {/* Cart Button for non-logged users */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/cart')}
                  className="relative h-9"
                  data-testid="nav-cart"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-[#008080] text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </Button>
                <Button variant="ghost" size="sm" className="h-9" onClick={() => navigate('/login')} data-testid="nav-login">
                  {t('login')}
                </Button>
                <Button
                  size="sm"
                  className="bg-[#008080] text-white hover:bg-[#006666] rounded-sm px-4 h-9 font-medium transition-all duration-300 shadow-md hover:shadow-lg"
                  onClick={() => navigate('/signup')}
                  data-testid="nav-signup"
                >
                  {t('signup')}
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden h-9"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        <div className="md:hidden pb-3">
          <form onSubmit={handleSearch} className="flex h-9">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  type="button"
                  variant="outline" 
                  size="sm"
                  className="rounded-r-none border-r-0 bg-slate-50 px-2 h-9"
                >
                  {searchType === 'products' ? (
                    <Package className="w-4 h-4" />
                  ) : (
                    <Users className="w-4 h-4" />
                  )}
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setSearchType('products')}>
                  <Package className="w-4 h-4 mr-2" />
                  Prodotti
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSearchType('crafters')}>
                  <Users className="w-4 h-4 mr-2" />
                  Artigiani
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cerca..."
              className="rounded-none border-x-0 h-9 text-sm flex-1"
            />
            
            <Button 
              type="submit"
              size="sm"
              className="rounded-l-none bg-[#008080] hover:bg-[#006666] px-3 h-9"
            >
              <Search className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            {/* Navigation Links */}
            <div className="space-y-1">
              <button
                onClick={() => handleNavClick('/')}
                className="flex items-center gap-3 w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-[#008080] rounded-lg font-medium transition-colors"
                data-testid="mobile-nav-home"
              >
                <Home className="w-5 h-5" />
                {t('home')}
              </button>
              <button
                onClick={() => handleNavClick('/products')}
                className="flex items-center gap-3 w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-[#008080] rounded-lg font-medium transition-colors"
                data-testid="mobile-nav-products"
              >
                <Package className="w-5 h-5" />
                Prodotti
              </button>
              <button
                onClick={() => handleNavClick('/crafters')}
                className="flex items-center gap-3 w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-[#008080] rounded-lg font-medium transition-colors"
                data-testid="mobile-nav-crafters"
              >
                <Users className="w-5 h-5" />
                Artigiani
              </button>
              <button
                onClick={() => handleNavClick('/requests')}
                className="flex items-center gap-3 w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-[#008080] rounded-lg font-medium transition-colors"
                data-testid="mobile-nav-requests"
              >
                <FileText className="w-5 h-5" />
                {t('requests')}
              </button>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-200 my-2"></div>

            {/* Language Switcher for Mobile - HIDDEN FOR ITALY-ONLY LAUNCH - Restore when implementing i18n */}
            {/* <div className="flex items-center gap-2 px-4 py-2">
              <Globe className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-500">Lingua:</span>
              <button
                onClick={() => setLanguage('it')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${language === 'it' ? 'bg-[#008080] text-white' : 'bg-slate-100 text-slate-700'}`}
              >
                IT
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${language === 'en' ? 'bg-[#008080] text-white' : 'bg-slate-100 text-slate-700'}`}
              >
                EN
              </button>
            </div> */}

            {/* Auth buttons for mobile (when not logged in) */}
            {!user && (
              <div className="space-y-2 pt-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleNavClick('/login')}
                  data-testid="mobile-nav-login"
                >
                  {t('login')}
                </Button>
                <Button
                  className="w-full bg-[#008080] text-white hover:bg-[#006666]"
                  onClick={() => handleNavClick('/signup')}
                  data-testid="mobile-nav-signup"
                >
                  {t('signup')}
                </Button>
              </div>
            )}

            {/* User specific links for mobile */}
            {user && (
              <div className="space-y-1 pt-2">
                <button
                  onClick={() => handleNavClick('/client/dashboard?tab=cliente')}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-[#00CED1] rounded-lg font-medium transition-colors"
                  data-testid="mobile-nav-area-cliente"
                >
                  <ShoppingBag className="w-5 h-5" />
                  Area Cliente
                </button>
                <button
                  onClick={() => handleNavClick('/crafter/dashboard?tab=artigiano')}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-[#DAA520] rounded-lg font-medium transition-colors"
                  data-testid="mobile-nav-area-artigiano"
                >
                  <Store className="w-5 h-5" />
                  Area Artigiano
                  {unreadProposals > 0 && (
                    <Badge className="ml-2 bg-[#DAA520] text-white text-xs">{unreadProposals}</Badge>
                  )}
                </button>
                <button
                  onClick={() => handleNavClick('/favorites')}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-red-500 rounded-lg font-medium transition-colors"
                  data-testid="mobile-nav-favorites"
                >
                  <Heart className="w-5 h-5" />
                  Preferiti
                </button>
                <button
                  onClick={() => handleNavClick('/messages')}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-[#008080] rounded-lg font-medium transition-colors"
                  data-testid="mobile-nav-messages"
                >
                  <MessageSquare className="w-5 h-5" />
                  Messaggi
                  {unreadMessages > 0 && (
                    <Badge className="ml-auto bg-red-500 text-white text-xs">{unreadMessages}</Badge>
                  )}
                </button>
                <button
                  onClick={() => handleNavClick('/profile')}
                  className="flex items-center gap-3 w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-[#008080] rounded-lg font-medium transition-colors"
                  data-testid="mobile-nav-profile"
                >
                  <User className="w-5 h-5" />
                  {t('profile')}
                </button>
                {profile?.is_admin && (
                  <button
                    onClick={() => handleNavClick('/admin')}
                    className="block w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors"
                    data-testid="mobile-nav-admin"
                  >
                    <Shield className="w-4 h-4 inline mr-2" />
                    SuperAdmin
                  </button>
                )}
                <button
                  onClick={handleSignOut}
                  className="block w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition-colors"
                  data-testid="mobile-nav-logout"
                >
                  <LogOut className="w-4 h-4 inline mr-2" />
                  {t('logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
